import { promiseState } from '@passionware/platform-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { EnterpriseTeam } from 'v5/api/enterprise/enterprise.api';
import { DEFAULT_TEAM_ICON_COLOR } from 'v5/api/enterprise/enterprise.logic';
import { ColorPicker } from 'v5/patterns/widgets/pickers/ColorPicker';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from 'v5/design-system/Form';
import { Form } from 'v5/design-system/Form';
import { Input } from 'v5/design-system/Input';
import { FormDialogContent } from 'v5/patterns/forms/dialogs/FormDialogContent';
import { useRegisterDirtyState } from 'v5/patterns/forms/unsaved-changes/useRegisterDirtyState';
import { WithServices } from 'v5/platform/ts/services';
import { WithToastService } from 'v5/services/front/ToastService/ToastService';
import { WithUploadService } from 'v5/services/io/UploadService/UploadService';
import { EnterpriseIconUploadField } from './EnterpriseIconUploadField';

export type EnterpriseTeamFormData = {
  name: string;
  iconUrl: string;
  iconColor: string;
};

export type EnterpriseTeamDialogContentProps = WithServices<
  [WithUploadService, WithToastService]
> & {
  team?: EnterpriseTeam;
  open?: boolean;
  onSubmit?: (data: EnterpriseTeamFormData) => Promise<void> | void;
};

export function EnterpriseTeamDialogContent({
  team,
  open = true,
  onSubmit,
  services
}: EnterpriseTeamDialogContentProps) {
  const isEdit = team != null;
  const { t } = useTranslation('core', { keyPrefix: 'enterprise' });
  const mutation = promiseState.useMutation(
    async (data: EnterpriseTeamFormData) => onSubmit?.(data)
  );

  const form = useForm<EnterpriseTeamFormData>({
    defaultValues: team
      ? {
          name: team.name,
          iconUrl: team.iconUrl,
          iconColor: team.iconColor
        }
      : {
          name: '',
          iconUrl: '',
          iconColor: DEFAULT_TEAM_ICON_COLOR
        }
  });

  useRegisterDirtyState(form.formState.isDirty);

  const name = form.watch('name');
  const iconColor = form.watch('iconColor');
  const previewFallback = name || team?.name || t('createTeam');

  return (
    <Form {...form}>
      <FormDialogContent
        open={open}
        title={t(isEdit ? 'editTeam' : 'createTeam')}
        description={t(
          isEdit ? 'editTeamDescription' : 'createTeamDescription'
        )}
        onSubmit={form.handleSubmit(data => mutation.track(data))}
        mutation={mutation.state}
        saveText={t(isEdit ? 'save' : 'create')}
        disabled={!name?.trim()}
      >
        <FormField
          control={form.control}
          name="name"
          rules={{ required: true }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('fieldName')}</FormLabel>
              <FormControl>
                <Input {...field} autoFocus />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="iconColor"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('fieldIconColor')}</FormLabel>
              <FormControl>
                <ColorPicker
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={t('pickIconColor')}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="iconUrl"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('fieldIcon')}</FormLabel>
              <FormControl>
                <EnterpriseIconUploadField
                  services={services}
                  value={field.value}
                  onChange={field.onChange}
                  previewName={previewFallback}
                  variant="team"
                  iconColor={iconColor}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </FormDialogContent>
    </Form>
  );
}
